import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    RefreshCw, Home, TrendingUp, Key, Star, Wallet, Award,
    Bookmark, MessageSquare, Activity, Search, Calculator, Users,
    HeartHandshake, Building2, HardHat, Landmark, ClipboardList,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    fetchUserProfileSegments, fetchUserProfileSegmentList,
} from '../api/analyticsApi';
import EngagementListModal from './EngagementListModal';

// Icon + accent per segment key, so each card reads at a glance.
const SEGMENT_STYLE = {
    // Self-reported persona (from the "Help GeeVee Personalise" popup).
    persona_home_buyer: { Icon: Home, accent: '#2563eb' },
    persona_investor: { Icon: TrendingUp, accent: '#0e9f6e' },
    persona_landlord: { Icon: Key, accent: '#7c3aed' },
    persona_buyers_advocate: { Icon: HeartHandshake, accent: '#db2777' },
    persona_real_estate_agent: { Icon: Building2, accent: '#0891b2' },
    persona_developer: { Icon: HardHat, accent: '#d97706' },
    persona_mortgage_broker: { Icon: Landmark, accent: '#dc2626' },
    landlord: { Icon: Key, accent: '#0e9f6e' },
    investor: { Icon: TrendingUp, accent: '#2563eb' },
    tenant: { Icon: Home, accent: '#7c3aed' },
    first_home: { Icon: Star, accent: '#d97706' },
    finance_ready: { Icon: Wallet, accent: '#0891b2' },
    advocate: { Icon: Award, accent: '#db2777' },
    saved_3plus: { Icon: Bookmark, accent: '#dc2626' },
    has_chat: { Icon: MessageSquare, accent: '#55C5D0' },
    active: { Icon: Activity, accent: '#7c3aed' },
    browsed: { Icon: Search, accent: '#2563eb' },
    used_calculator: { Icon: Calculator, accent: '#d97706' },
    finance_ready_active: { Icon: Wallet, accent: '#0891b2' },
    landlord_used_calculator: { Icon: Calculator, accent: '#0e9f6e' },
};

const SegmentCard = ({ seg, onClick }) => {
    const style = SEGMENT_STYLE[seg.key] || { Icon: Users, accent: '#6b7280' };
    const { Icon, accent } = style;
    const clickable = !!onClick && seg.count > 0;
    return (
        <button
            type="button"
            onClick={clickable ? onClick : undefined}
            disabled={!clickable}
            className={`text-left bg-white rounded-xl border border-gray-200 p-4 w-full transition-all ${
                clickable ? 'hover:shadow-md hover:border-gray-300 cursor-pointer' : 'cursor-default'
            }`}
        >
            <div className="flex items-center gap-2 mb-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg"
                      style={{ background: `${accent}1a`, color: accent }}>
                    <Icon className="w-4 h-4" />
                </span>
                <span className="text-xs font-medium text-gray-500">{seg.label}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{(seg.count || 0).toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-0.5">{seg.pct ?? 0}% of buyers</div>
        </button>
    );
};

const Card = ({ title, Icon, iconColor, children, subtitle }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Icon className="w-4 h-4" style={{ color: iconColor }} /> {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-400 mb-4">{subtitle}</p>}
        <div className={subtitle ? '' : 'mt-4'}>{children}</div>
    </div>
);

// Accent per persona branch, matching the persona segment cards above.
const PERSONA_BRANCH_ACCENT = {
    landlord: '#7c3aed',
    buyers_advocate: '#db2777',
    investor: '#0e9f6e',
};

// One answer option as a labelled progress bar. Clickable (when anyone picked it)
// to drill into the buyers who gave that answer, via the shared segment modal.
const AnswerBar = ({ option, answered, accent, onClick }) => {
    const pct = answered ? Math.round((option.count / answered) * 100) : 0;
    const clickable = option.count > 0 && !!onClick;
    return (
        <button
            type="button"
            onClick={clickable ? onClick : undefined}
            disabled={!clickable}
            className={`w-full text-left ${clickable ? 'cursor-pointer group' : 'cursor-default'}`}
        >
            <div className="flex items-center justify-between text-xs mb-1">
                <span className={`text-gray-600 ${clickable ? 'group-hover:text-gray-900' : ''}`}>{option.value}</span>
                <span className="text-gray-400 tabular-nums shrink-0 ml-2">
                    {option.count.toLocaleString()} · {pct}%
                </span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full transition-all"
                     style={{ width: `${pct}%`, background: accent, minWidth: option.count > 0 ? 4 : 0 }} />
            </div>
        </button>
    );
};

// One branch (landlord / advocate / investor) of follow-up answers.
const PersonaBranchCard = ({ branch, onOption }) => {
    const accent = PERSONA_BRANCH_ACCENT[branch.persona] || '#6b7280';
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg"
                      style={{ background: `${accent}1a`, color: accent }}>
                    <ClipboardList className="w-4 h-4" />
                </span>
                <h3 className="text-sm font-semibold text-gray-900">{branch.label}</h3>
                <span className="text-xs text-gray-400 ml-auto">
                    {branch.respondents.toLocaleString()} respondent{branch.respondents === 1 ? '' : 's'}
                </span>
            </div>

            <div className="space-y-5">
                {branch.questions.map((q) => (
                    <div key={q.key}>
                        <div className="text-xs font-medium text-gray-700 mb-2">{q.label}</div>

                        {q.type === 'text' ? (
                            q.responses.length === 0 ? (
                                <p className="text-xs text-gray-400 italic">No responses yet</p>
                            ) : (
                                <ul className="space-y-1.5">
                                    {q.responses.map((r, i) => (
                                        <li key={i} className="text-xs flex gap-2">
                                            <span className="text-gray-400 shrink-0 max-w-[40%] truncate">{r.name}</span>
                                            <span className="text-gray-800">{r.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            )
                        ) : q.answered === 0 ? (
                            <p className="text-xs text-gray-400 italic">No answers yet</p>
                        ) : (
                            <div className="space-y-2">
                                {q.options.map((opt) => (
                                    <AnswerBar
                                        key={opt.value}
                                        option={opt}
                                        answered={q.answered}
                                        accent={accent}
                                        onClick={() => onOption(q, opt)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const GROUP_SUBTITLE = {
    persona: 'Self-reported in the GeeVee popup, combined with CRM signals (e.g. contacts flagged Buyer Advocate, or typed landlord/investor) · click a card for the list',
    status: 'Current CRM state — not affected by the date range · click a card for the list',
    activity: 'Within the selected date range · click a card for the list',
    cross: 'Combined segments — the high-value lists · click a card for the list',
};

const UserProfilesPanel = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadTokenRef = useRef(0);

    const [listModal, setListModal] = useState({ open: false, loading: false, title: '', subtitle: '', rows: [] });

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        fetchUserProfileSegments(startDate, endDate)
            .then((d) => { if (myToken === loadTokenRef.current) setData(d); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error(err);
                toast.error('Failed to load user profiles');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    const openSegment = useCallback(async (seg) => {
        setListModal({ open: true, loading: true, title: seg.label, subtitle: `${seg.count} buyer${seg.count === 1 ? '' : 's'}`, rows: [] });
        try {
            const res = await fetchUserProfileSegmentList(startDate, endDate, seg.key);
            setListModal((m) => ({ ...m, loading: false, rows: res?.rows || [] }));
        } catch (err) {
            console.error('Segment drill-down failed:', err);
            toast.error('Failed to load list');
            setListModal((m) => ({ ...m, loading: false, rows: [] }));
        }
    }, [startDate, endDate]);

    // Drill into the buyers who gave one persona follow-up answer. Reuses the
    // segment list endpoint via the answer option's `segment_key` (pa:*).
    const openPersonaOption = useCallback((question, option) => {
        openSegment({
            key: option.segment_key,
            label: `${question.label}: ${option.value}`,
            count: option.count,
        });
    }, [openSegment]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading user profiles…
            </div>
        );
    }

    const groups = data?.groups || [];

    // Branch-specific follow-up answers, rendered directly under the persona group.
    const answerBranches = (data?.persona_answers || []).filter((b) => b.respondents > 0);
    const personaAnswersSection = answerBranches.length === 0 ? null : (
        <div className="space-y-3">
            <div>
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-[#2563eb]" /> Persona follow-up answers
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                    Branch-specific questions from the GeeVee personalisation popup · click an answer for the buyer list
                </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start">
                {answerBranches.map((branch) => (
                    <PersonaBranchCard key={branch.persona} branch={branch} onOption={openPersonaOption} />
                ))}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4 text-gray-400" />
                <span><strong className="text-gray-900">{(data?.total_buyers || 0).toLocaleString()}</strong> portal buyers in the unified profile base</span>
            </div>

            {groups.map((group) => (
                <React.Fragment key={group.group}>
                    <Card title={group.label} Icon={Users} iconColor="#2563eb"
                          subtitle={GROUP_SUBTITLE[group.group]}>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            {group.segments.map((seg) => (
                                <SegmentCard key={seg.key} seg={seg} onClick={() => openSegment(seg)} />
                            ))}
                        </div>
                    </Card>
                    {/* Persona follow-up answers sit under the persona group.
                        Investment goals moved to the Buyer Analytics tab. */}
                    {group.group === 'persona' && personaAnswersSection}
                </React.Fragment>
            ))}

            <EngagementListModal
                isOpen={listModal.open}
                onClose={() => setListModal((m) => ({ ...m, open: false }))}
                title={listModal.title}
                subtitle={listModal.subtitle}
                loading={listModal.loading}
                rows={listModal.rows}
                variant="buyers"
            />
        </div>
    );
};

export default UserProfilesPanel;
